import { useState, useEffect, useMemo } from "react";
import { useParams } from 'react-router-dom';
import requests from "../helpers/requests";
import ReactPlayer from 'react-player'
import ReactAudioPlayer from 'react-audio-player';
import ApexChart from "./visualization/Chart"
import { Grid, Tab, Box, Button, Skeleton, TextField, Autocomplete, Snackbar, Alert, Tabs, ButtonGroup, Typography, Tooltip } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import BasicTable from "./visualization/Table";
import AmountTable from "./visualization/Table2";
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import zIndex from "@mui/material/styles/zIndex";
import language from "../languages/languages";
import TimelineChart from "./visualization/Charts2";
import options from "../helpers/labels";
import ValidationForm from "../Validation/ValidationForm";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DropdownShareButton from "./Share";
import 'onsenui/css/onsen-css-components.css';
import { GestureDetector } from 'react-onsenui';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MovementCard from "../Statistics/MovementCard";
import "./stations.css";

const MOVEMENT_PAGE_SIZE = 30;

const displayTimestamp = (value) => {
  if (typeof value === "string") {
    return value.split(".")[0];
  }
  return value || "";
};

function StationView(props) {

  const { id } = useParams()
  const { user, token } = useAuth();
  const [data, setData] = useState("");
  const [temperature, setTemperature] = useState( [{
    label: "temperature",
    data: []
  }]);
  const [humidity, setHumidity] = useState([{
    label: "temperature",
    data: []
  }]);
  const [pressure, setPressure] = useState([{
    label: "temperature",
    data: []
  }]);
  const [illuminance, setIlluminance] = useState([{
    label: "temperature",
    data: []
  }]);
  const [value, setValue] = useState(0);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState(new Date(Date.now()))
  const [day, setDay] = useState(null)
  const [searchValue, setSearchValue] = useState("")
  const navigate = useNavigate();
  const [counter, setCounter] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [bird, setBird] = useState("") 
  const [selectedButton, setSelectedButton] = useState("1");
  const [movementMeta, setMovementMeta] = useState({
    limit: MOVEMENT_PAGE_SIZE,
    offset: 0,
    returned: 0,
    total: 0,
    hasMore: false,
    hasPrevious: false,
    loading: false,
    loadedAll: true,
  });
  const [movementSource, setMovementSource] = useState('station');
  const [movementDeletion, setMovementDeletion] = useState({ loading: false, movementId: null });
  const [deleteFeedback, setDeleteFeedback] = useState({ open: false, message: '', severity: 'success' });
  const stationCopy = language[props.language]?.stations || language.en.stations;
  const canDeleteMovements = Boolean(user && data && (user.isAdmin || (data.ownerId && user.id === data.ownerId)));
  const getWeightDisplay = (weightValue) => {
    if (typeof weightValue === "number" && Number.isFinite(weightValue)) {
      return `${weightValue.toFixed(0)} gramm`;
    }
    return stationCopy.weightUnavailable || language.en.stations.weightUnavailable || "";
  };
  const specialBirds = useMemo(() => {
    if (!data || !data.statisticsSummary) {
      return [];
    }
    const highlights = data.statisticsSummary.specialBirds;
    if (!Array.isArray(highlights)) {
      return [];
    }
    return highlights.filter((entry) => entry && Array.isArray(entry.movements) && entry.movements.length > 0);
  }, [data]);

  const specialBirdsUpdatedAt = useMemo(() => {
    const timestamp = data?.statisticsSummary?.updatedAt;
    if (!timestamp || typeof timestamp !== "string") {
      return null;
    }
    return displayTimestamp(timestamp);
  }, [data]);

  const specialHighlightsUpdatedText = useMemo(() => {
    if (!specialBirdsUpdatedAt) {
      return null;
    }
    const template = stationCopy.specialHighlightsUpdated || language.en.stations.specialHighlightsUpdated;
    if (!template) {
      return specialBirdsUpdatedAt;
    }
    return template.replace("{date}", specialBirdsUpdatedAt);
  }, [specialBirdsUpdatedAt, stationCopy]);

  const showDeleteFeedback = (message, severity = 'success') => {
    setDeleteFeedback({ open: true, message, severity });
  };

  const handleDeleteFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setDeleteFeedback((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteMovement = async (movementId) => {
    if (!canDeleteMovements) {
      showDeleteFeedback(
        stationCopy.deleteMovementLoginRequired || language.en.stations.deleteMovementLoginRequired,
        'warning'
      );
      return;
    }
    if (!token) {
      showDeleteFeedback(
        stationCopy.deleteMovementLoginRequired || language.en.stations.deleteMovementLoginRequired,
        'warning'
      );
      return;
    }
    const confirmText = stationCopy.deleteMovementConfirm || language.en.stations.deleteMovementConfirm;
    const confirmed = window.confirm(confirmText);
    if (!confirmed) {
      return;
    }
    try {
      setMovementDeletion({ loading: true, movementId });
      await requests.deleteMovement(id, movementId, token);
      showDeleteFeedback(
        stationCopy.deleteMovementSuccess || language.en.stations.deleteMovementSuccess,
        'success'
      );
      getStation();
    } catch (error) {
      const apiMessage = error?.response?.data?.message || error?.response?.data;
      showDeleteFeedback(
        apiMessage || stationCopy.deleteMovementError || language.en.stations.deleteMovementError,
        'error'
      );
    } finally {
      setMovementDeletion({ loading: false, movementId: null });
    }
  };

  useEffect(() => {
    getStation();


  }, [])

  useEffect(() => {
    counter > 0 && setTimeout(() => changeCounter(counter - 1), 1000);
  }, [counter]);


  const getStation = ({ limit = MOVEMENT_PAGE_SIZE, offset = 0, refreshEnvironment = true, selectTab = 0 } = {}) => {
    setMovementSource('station');
    setMovementMeta((prev) => ({ ...prev, loading: true }));
    requests.getStation(id, { movements: limit, movementsOffset: offset })
      .then((res) => {
        const fetchedMovements = res.data?.measurements?.movements || [];
        setData(res.data);
        setValue(selectTab === 'last' ? Math.max(0, fetchedMovements.length - 1) : selectTab);
        if (refreshEnvironment) {
          getEnvironment(selectedButton);
        }
        const meta = res.data?.movementsMeta;
        if (meta) {
          const metaOffset = meta.offset ?? offset;
          setMovementMeta({
            limit: meta.limit ?? limit,
            offset: metaOffset,
            returned: meta.returned ?? fetchedMovements.length,
            total: meta.total ?? fetchedMovements.length,
            hasMore: Boolean(meta.hasMore),
            hasPrevious: metaOffset > 0,
            loading: false,
            loadedAll: !meta.hasMore,
          });
        } else {
          setMovementMeta({
            limit,
            offset,
            returned: fetchedMovements.length,
            total: fetchedMovements.length,
            hasMore: false,
            hasPrevious: offset > 0,
            loading: false,
            loadedAll: true,
          });
        }
      }).catch(err => {
        // Handle error
        let text = language[props.language]["stations"]["notFound"]
        setText(text)
        setOpen(true)
        changeCounter(5)
        const myTimeout = setTimeout(routeToHome, 5000)
      })
      .finally(() => {
        setMovementMeta((prev) => ({ ...prev, loading: false }));
      });
  }

  const handleLoadNextMovementsPage = () => {
    if (movementMeta.loading || movementSource !== 'station' || !movementMeta.hasMore) {
      return;
    }
    getStation({
      limit: movementMeta.limit || MOVEMENT_PAGE_SIZE,
      offset: movementMeta.offset + (movementMeta.limit || MOVEMENT_PAGE_SIZE),
      refreshEnvironment: false,
      selectTab: 0,
    });
  }

  const handleLoadPreviousMovementsPage = () => {
    if (movementMeta.loading || movementSource !== 'station' || !movementMeta.hasPrevious) {
      return;
    }
    const limit = movementMeta.limit || MOVEMENT_PAGE_SIZE;
    getStation({
      limit,
      offset: Math.max(0, movementMeta.offset - limit),
      refreshEnvironment: false,
      selectTab: 'last',
    });
  }

  function changeCounter(count) {
    setCounter(count)
    //let text = language[props.language]["stations"]["notFound"] 
    //setText(text)
  }

  const handleButtonClick = (buttonId) => {
    setSelectedButton(buttonId);
    getEnvironment(buttonId)
  };


  function routeToHome() {
    navigate("/view")
  }

  const getEnvironment = (months) => {

    requests.getEnvironment(id,months)
      .then((res) => {
        var data = res.data
        //console.log(res);
        createSeries(res.data)
      })
  }

  const createSeries = (environment) => {
    console.log("triggerd")

    var tempSeries = [{
      label: "temperature",
      data: [

      ]
    }]
    var humSeries = [{
      label: "humidity",
      data: [
      ]
    }]

    var pressSeries = [{
      label: "airpressure",
      data: [
      ]
    }]

    var illumanceSeries = [{
      label: "illuminance",
      data: [
      ]
    }]

    if (environment.length === 0) {
      setTemperature(tempSeries)
      setHumidity(humSeries)
      setPressure(pressSeries)
      setIlluminance(illumanceSeries)

    }

    else {
      for (var i = 0; i < environment.length; i = i + 5) {

        var env = environment[i]

        let date = env.date;
        const dateArray = date.split(".");
        date = new Date(dateArray[0].replace(/\s/, 'T'))
        var hum = env.humidity
        var temp = env.temperature
        if(env.airpressure){
          pressSeries[0].data.push({ x: date, y: env.airpressure })
        }
        if(env.illuminance){
          illumanceSeries[0].data.push({ x: date, y: env.illuminance })
        }
        try {
          var lastDate = tempSeries[0].data[tempSeries[0].data.length - 1].x
          var diffTime = Math.abs(date - lastDate);
          if (diffTime > 1000 * 60 * 240) {
            var middleDate = new Date(lastDate + 1000 * 60 * 120)
            tempSeries[0].data.push({ x: middleDate, y: null })
            humSeries[0].data.push({ x: middleDate, y: null })

          }
        }
        catch (e) {
          //console.log(e)
        }
        if (temp > -35) {
          tempSeries[0].data.push({ x: date, y: temp })
          humSeries[0].data.push({ x: date, y: hum })
        }

      }

      setTemperature(tempSeries)
      setHumidity(humSeries)
      if(pressSeries[0].data.length == 0){
        pressSeries[0].data = false
      }
      if(illumanceSeries[0].data.length == 0){
        illumanceSeries[0].data = false
      }
      setPressure(pressSeries)
      setIlluminance(illumanceSeries)


    }



  }


  const handleChange = (event, newValue) => {
    setValue(newValue);
    setBird("")
  };

  const handleClickOpen = (area) => {
    //console.log(area)
    if (area == "species") {
      setText(language[props.language]["stations"]["infospecies"])
    }
    else if (area == "count") {
      setText(language[props.language]["stations"]["infocount"])
    }
    else if (area == "environment") {
      setText(language[props.language]["stations"]["infoenvironment"])
    }

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const searchForSpecies = (value) => {
    let search = null
    //console.log(value)
    if (!value && searchValue) {
      search = options.saerchOptions[searchValue]["latinName"]
    }
    else if (value) {
      search = value
    }

    let searchDay = null
    if (day) {
      searchDay = day.format("YYYY-MM-DD")
    }
    setMovementSource('search');
    setMovementMeta((prev) => ({ ...prev, loading: true }));
    requests.searchForSpecies(id, search, 30, searchDay)
      .then((res) => {
        setData((prev) => {
          const base = (prev && typeof prev === 'object') ? prev : {};
          return {
            ...base,
            measurements: {
              ...(base.measurements || {}),
              movements: res.data,
            },
          };
        });
        setMovementMeta({
          limit: 0,
          offset: 0,
          returned: res.data.length,
          total: res.data.length,
          hasMore: false,
          hasPrevious: false,
          loading: false,
          loadedAll: true,
        });
      })
      .catch(() => {
        setMovementMeta((prev) => ({ ...prev, loading: false }));
      })
  }

  function handleInputChange(event, value) {
    ////console.log(value);
    setSearchValue(value)
  }

  function handelSearchChange(value) {

    setSearchValue(value);
    searchForSpecies(value);
  }

  const handleClick = () => {
    setSnackbarOpen(true);
  };

  const handleToClose = (event, reason) => {
    if ("clickaway" == reason) return;
    setSnackbarOpen(false);
  };

  const sendValidation = () => {
    let validation = { validation: {} }
    let valBird = options.validationOptions[bird]
    if (!valBird) {
      valBird = { "latinName": bird, "germanName": "" }
    }
    validation.validation = valBird
    requests.sendValidation(data["measurements"]["movements"][value]["station_id"], data["measurements"]["movements"][value]["mov_id"], validation)
    valBird.amount=2
    let  newData = data
    let latinName = valBird["latinName"]
    newData["measurements"]["movements"][value]["validation"] = {"summary" : {latinName : valBird}}
    console.log(newData)
    setData(newData)

    handleClick();

  }

  const sendValidationNone = () => {
    let validation = { validation: { "latinName": "None", "germanName": "" } }
    requests.sendValidation(data["measurements"]["movements"][value]["station_id"], data["measurements"]["movements"][value]["mov_id"], validation)
    handleClick();

  }


  return <div>

    <Button variant="contained" onClick={() => { getStation() }} sx={{ display: { xs: 'none', md: 'block' } }} style={{ margin: "15px", position: "absolute", right: "25px", zIndex: "10000" }}>Refresh</Button>
    <h1 style={{ textAlign: "center" }}>Station: {data ? data.name : id}</h1>
    {data && data.description ? (
      <div style={{ maxWidth: "700px", margin: "0 auto 20px", textAlign: "center" }}>
        <h3>{stationCopy.descriptionTitle || language.en.stations.descriptionTitle}</h3>
        <p style={{ whiteSpace: "pre-wrap" }}>{data.description}</p>
      </div>
    ) : null}
    <Grid container spacing={2} style={{ marginLeft: "20px" }}>
      <Grid item lg={3}>
        <Autocomplete
          id="combo-box-demo"
          options={Object.keys(options.saerchOptions)}
          sx={{ width: 300 }}
          onInputChange={handleInputChange}
          value={searchValue}
          renderInput={(params) => <TextField {...params} label={language[props.language]["stations"]["search"]} />}
        />
      </Grid>
      <Grid item lg={3}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disableFuture
            inputFormat="YYYY-MM-DD"
            label={language[props.language]["stations"]["day"]}
            value={day}
            onChange={(newValue) => {
              setDay(newValue);
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item lg={1}>
        <Button disabled={!data} onClick={() => searchForSpecies("")}> {language[props.language]["stations"]["search2"]}</Button>
      </Grid>
      <Grid item lg={3}>
      </Grid>
      <Grid item lg={2}>
      <Button variant="contained" component={Link} to={data.type == "exhibit" ? "/view/statistics" : "/view/statistics/" + id}>{language[props.language]["statistics"]["show"]}</Button>
      </Grid>

    </Grid>


    {data ?
      <div style={{padding: "10px"}}>
        {data.measurements.movements && data.measurements.movements.length > 0 ?
          <div>
            <TabContext value={value}>
              <Box sx={{ bgcolor: 'background.paper', display: 'flex', alignItems: 'center' }} style={{ maxWidth: "94vw", marginLeft: "3vw" }}>

                {movementSource === 'station' && movementMeta.hasPrevious ? (
                  <Tooltip title={language[props.language]["stations"]["loadPreviousMovements"] || language.en.stations.loadPreviousMovements}>
                    <span>
                      <IconButton
                        onClick={handleLoadPreviousMovementsPage}
                        disabled={movementMeta.loading}
                        aria-label={language[props.language]["stations"]["loadPreviousMovements"] || language.en.stations.loadPreviousMovements}
                        sx={{ flexShrink: 0 }}
                      >
                        <KeyboardDoubleArrowLeftIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TabList
                    value={value}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="scrollable auto tabs example"
                  >
                    {data.measurements.movements.map((movement) =>
                      <Tab label={displayTimestamp(movement.start_date)}></Tab>
                    )}
                  </TabList>
                </Box>
                {movementSource === 'station' && movementMeta.hasMore ? (
                  <Tooltip title={language[props.language]["stations"]["loadMoreMovements"]}>
                    <span>
                      <IconButton
                        onClick={handleLoadNextMovementsPage}
                        disabled={movementMeta.loading}
                        aria-label={language[props.language]["stations"]["loadMoreMovements"]}
                        sx={{ flexShrink: 0 }}
                      >
                        <KeyboardDoubleArrowRightIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>

              {data.measurements.movements.map((movement, i) =>
                <TabPanel value={i}>
                  <GestureDetector
                onSwipeLeft={() => {
                  if (value < data.measurements.movements.length - 1) {
                    setValue(value + 1);
                  } else {
                    handleLoadNextMovementsPage();
                  }
                }}
                onSwipeRight={() => {
                  if (value > 0) {
                    setValue(value - 1);
                  } else {
                    handleLoadPreviousMovementsPage();
                  }
                }}
                onDragLeft={() => {
                  if (value < data.measurements.movements.length - 1) {
                    setValue(value + 1);
                  } else {
                    handleLoadNextMovementsPage();
                  }
                }}
                onDragRight={() => {
                  if (value > 0) {
                    setValue(value - 1);
                  } else {
                    handleLoadPreviousMovementsPage();
                  }
                }}
            >
                  <Grid container spacing={4}>
                    <Grid item lg={8}>
                      {movement.video == "pending" ? < div><p>{language[props.language]["stations"]["wait1"]}<br />  </p> <Button variant="contained" onClick={() => { getStation() }} style={{ margin: "15px" }}>Refresh</Button></div>
                        : !movement.video ? <p>{stationCopy.videoDeleted || language.en.stations.videoDeleted}</p>
                        :
                        <ReactPlayer playing={true} playsinline url={movement.video} loop={true} controls={true} width="100%" height="min(95vw, 80vh)" style={{ aspectRatio: 1 }} />}
                    </Grid>
                    <Grid item lg={4}>
                      <DropdownShareButton language={props.language} station_id={id} mov_id={movement.mov_id}></DropdownShareButton>
                      <h4>{language[props.language]["stations"]["audio"]}</h4>
                      <ReactAudioPlayer src={movement.audio} controls />
                      <h4>{language[props.language]["stations"]["weight"]}</h4>
                      <p>{getWeightDisplay(movement.weight)}</p>
                      <IconButton color="primary" aria-label="upload picture" component="span" onClick={() => { handleClickOpen("species") }} style={{ float: "right" }}>
                        <InfoOutlinedIcon />
                      </IconButton>
                      <h4>{language[props.language]["stations"]["species"]}</h4>

                      <BasicTable birds={movement.detections} finished={movement.video} getStation={getStation} language={props.language} setBird={setBird} bird={bird} validation={movement.validation}></BasicTable>
                      <br />
                      <ValidationForm setBird={setBird} bird={bird} language={props.language} />
                      <br></br>
                      <Button variant="contained" onClick={sendValidation} style={{ marginRight: "5px", marginBottom: "5px" }}>{language[props.language]["validation"]["send"]}</Button>
                      <Button variant="contained" onClick={sendValidationNone} style={{ marginRight: "5px", marginBottom: "5px" }}>{language[props.language]["validation"]["noBird"]}</Button>
                      {canDeleteMovements && movement.station_id === id ? (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteMovement(movement.mov_id)}
                          disabled={movementDeletion.loading && movementDeletion.movementId === movement.mov_id}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          {(movementDeletion.loading && movementDeletion.movementId === movement.mov_id)
                            ? (stationCopy.deleteMovementPending || language.en.stations.deleteMovementPending || stationCopy.deleteMovement || language.en.stations.deleteMovement)
                            : (stationCopy.deleteMovement || language.en.stations.deleteMovement)}
                        </Button>
                      ) : null}
                      {data.type == "exhibit" && movement.station_id != id ? <div> <h4>{language[props.language]["stations"]["exhibitInfo"]} </h4> <span> {language[props.language]["stations"]["exhibitInfo2"]} <a href={'/view/station/' +movement.station_id} >{movement.station_name}</a>  {language[props.language]["stations"]["exhibitInfo3"] } </span> </div> :""
                       }
                    </Grid>
                  </Grid>
                  </GestureDetector>
                </TabPanel>)}
            </TabContext>
            {movementSource === 'station' && (movementMeta.hasPrevious || movementMeta.hasMore) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                {movementMeta.hasPrevious ? (
                  <Button variant="outlined" onClick={handleLoadPreviousMovementsPage} disabled={movementMeta.loading}>
                    {movementMeta.loading ? language[props.language]["stations"]["loadingMovements"] : language[props.language]["stations"]["loadPreviousMovements"] || language.en.stations.loadPreviousMovements}
                  </Button>
                ) : null}
                {movementMeta.hasMore ? (
                  <Button variant="outlined" onClick={handleLoadNextMovementsPage} disabled={movementMeta.loading}>
                    {movementMeta.loading ? language[props.language]["stations"]["loadingMovements"] : language[props.language]["stations"]["loadMoreMovements"]}
                  </Button>
                ) : null}
              </Box>
            ) : movementSource === 'station' && !movementMeta.hasMore && data && data.measurements && data.measurements.movements && data.measurements.movements.length > 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  {language[props.language]["stations"]["allMovementsLoaded"]}
                </Typography>
              </Box>
            ) : movementSource === 'search' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, px: 2 }}>
                <Typography variant="caption" color="textSecondary" align="center">
                  {language[props.language]["stations"]["searchResultsLoaded"] || language.en.stations.searchResultsLoaded || 'Showing all matching movements for the current filters.'}
                </Typography>
              </Box>
            ) : null}
          </div>
          : <p>{language[props.language]["stations"]["noData1"]}</p>}
        {specialBirds.length > 0 ? (
          <div className="station-special-birds">
            <div className="station-special-birds__header">
              <h3>{stationCopy.specialHighlightsTitle || language.en.stations.specialHighlightsTitle}</h3>
              {specialHighlightsUpdatedText ? (
                <span className="station-special-birds__timestamp">{specialHighlightsUpdatedText}</span>
              ) : null}
            </div>
            <p className="station-special-birds__description">
              {stationCopy.specialHighlightsDescription || language.en.stations.specialHighlightsDescription}
            </p>
            <Grid container spacing={2}>
              {specialBirds.map((bird, index) => (
                <Grid item lg={3} md={4} sm={6} xs={12} key={`station-special-${bird.latinName || index}`}>
                  <div className="station-special-birds__card">
                    <h5>
                      {bird.germanName ? bird.germanName : bird.latinName}
                      {bird.germanName && bird.latinName ? ` (${bird.latinName})` : ""}
                    </h5>
                    <MovementCard language={props.language} movement={bird.movements} />
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>
        ) : data?.statisticsSummary ? (
          <div className="station-special-birds station-special-birds--empty">
            <h3>{stationCopy.specialHighlightsTitle || language.en.stations.specialHighlightsTitle}</h3>
            <p className="station-special-birds__description">
              {stationCopy.specialHighlightsEmpty || language.en.stations.specialHighlightsEmpty}
            </p>
          </div>
        ) : null}
          {
            data.lastFeedStatus? <span> {language[props.language]["map"]["lastFeedStatus"]}
            {data.lastFeedStatus.silolevel+ " %" + " am " + displayTimestamp(data.lastFeedStatus.date)}<br/> </span>: ""
          }
        {temperature[0] ?
          (temperature[0].data.length > 0 ?
            <div>     <h3 style={{ "marginBlockEnd": "0px" }}>{language[props.language]["stations"]["environment"]}
            
              <IconButton color="primary" aria-label="upload picture" component="span" onClick={() => { handleClickOpen("environment") }} >
                <InfoOutlinedIcon />
              </IconButton></h3>
              <h4> {language[props.language]["stations"]["timespan"]}
              <ButtonGroup variant="contained" aria-label="outlined primary button group">
  <Button  onClick={() => handleButtonClick("1")} 
  variant={selectedButton== "1"? "outlined" : "contained"}>{language[props.language]["stations"]["button1"]}</Button>
  <Button  onClick={() => handleButtonClick("3")}
        variant={selectedButton== "3"? "outlined" : "contained"}>{language[props.language]["stations"]["button2"]}</Button>
  <Button  onClick={() => handleButtonClick("all")}
        variant={selectedButton== "all"? "outlined" : "contained"} >{language[props.language]["stations"]["button3"]}</Button>
</ButtonGroup></h4>
              <Grid container spacing={2}>
                <Grid item lg={6} md={12}>
                  <h4>{language[props.language]["stations"]["temperature"]}</h4><TimelineChart series={temperature} />
                </Grid>
                <Grid item lg={6} md={12}>
                  <h4>{language[props.language]["stations"]["humidity"]}</h4><TimelineChart series={humidity} />
                </Grid>
                {pressure && pressure[0].data ?
                <Grid item lg={6} md={12}>
                  <h4>{language[props.language]["stations"]["pressure"]}</h4><TimelineChart series={pressure} />
                </Grid>: ""}
                {illuminance && illuminance[0].data ? 
                <Grid item lg={6} md={12}>
                  <h4>{language[props.language]["stations"]["illuminance"]}</h4><TimelineChart series={illuminance} />
                </Grid> : ""}
              </Grid> </div> : <p>{language[props.language]["stations"]["noData2"]}</p>) :
          <div> <Grid container spacing={2}>
            <Grid item lg={6}>
              <h4>{language[props.language]["stations"]["temperature"]}:</h4> <Skeleton variant="rectangular" width={"100%"} height="350px" />
            </Grid>
            <Grid item lg={6}>
              <h4>{language[props.language]["stations"]["humidity"]}</h4><Skeleton variant="rectangular" width={"100%"} height="350px" />
            </Grid>
          </Grid> </div>
        }
        <h3 style={{ "marginBlockEnd": "0px" }}>{language[props.language]["stations"]["birdsCount"]}
          <IconButton color="primary" aria-label="upload picture" component="span" onClick={() => { handleClickOpen("count") }} >
            <InfoOutlinedIcon />
          </IconButton></h3>
        {data.count ?
          <div> <Grid container spacing={2}>
            <Grid item lg={6}>
              <h4>{language[props.language]["stations"]["today"]}</h4>
              <AmountTable birds={data.count[date.toISOString().split('T')[0]]? data.count[date.toISOString().split('T')[0]].birds : null} date={date.toISOString().split('T')[0]} language={props.language} handelSearchChange={handelSearchChange}></AmountTable>
            </Grid>
            <Grid item lg={6}>
              <h4>{language[props.language]["stations"]["yesterday"]} </h4>
              <AmountTable birds={data.count[new Date(date.getTime() - 1000 * 60 * 60 * 24).toISOString().split('T')[0]]? data.count[new Date(date.getTime() - 1000 * 60 * 60 * 24).toISOString().split('T')[0]]["birds"] : null} date={new Date(date.getTime() - 1000 * 60 * 60 * 24).toISOString().split('T')[0]} language={props.language} handelSearchChange={handelSearchChange}></AmountTable>
            </Grid>
          </Grid> </div> : <p>{language[props.language]["stations"]["noData2"]}</p>
        }
        <br></br>
        <br></br>
      </div> :
      <div>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleChange} aria-label="lab API tabs example" centered>
              <Tab label={"Tab1"} value="1" />
            </TabList>
          </Box>
          <TabPanel value="1">
            <Grid container spacing={4}>
              <Grid item lg={8}>
                <Skeleton variant="rectangular" width={"100%"} height="70vh" />
              </Grid>
              <Grid item lg={4}>

                <h4> {language[props.language]["stations"]["audio"]}</h4>
                <Skeleton variant="rectangular" width={"300px"} height="45px" />
                <h4>{language[props.language]["stations"]["weight"]}</h4>
                <Skeleton variant="text" />
                <h4>{language[props.language]["stations"]["species"]}</h4>
                <Skeleton variant="rectangular" width={"100%"} height="45px" />

              </Grid>
            </Grid>
          </TabPanel>
        </TabContext>
        <div> <Grid container spacing={2}>
          <Grid item lg={6}>
            <h4>{language[props.language]["stations"]["temperature"]}:</h4> <Skeleton variant="rectangular" width={"100%"} height="350px" />
          </Grid>
          <Grid item lg={6}>
            <h4>{language[props.language]["stations"]["humidity"]}</h4><Skeleton variant="rectangular" width={"100%"} height="350px" />
          </Grid>
        </Grid> </div>
        <div> <Grid container spacing={2}>
          <Grid item lg={6}>
            <h4>{language[props.language]["stations"]["today"]}</h4>
            <Skeleton variant="rectangular" width={"100%"} height="45px" />
          </Grid>
          <Grid item lg={6}>
            <h4>{language[props.language]["stations"]["yesterday"]}</h4>
            <Skeleton variant="rectangular" width={"100%"} height="45px" /></Grid>
        </Grid> </div>
        <br></br>

      </div>}
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {"Information"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description" style={{ "padding": "10px" }}>
          <p style={{ textAlign: "center" }}>{text}  <br /> <span style={{ textAlign: "center", fontSize: 20, fontWeight: 700 }}>{counter} </span></p>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
    <Snackbar open={snackbarOpen} onClose={handleToClose} autoHideDuration={6000} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} >
      <Alert severity="success" onClose={handleToClose} sx={{ width: '100%' }}>
        Validation send!
      </Alert>
    </Snackbar>
    <Snackbar open={deleteFeedback.open} onClose={handleDeleteFeedbackClose} autoHideDuration={6000} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
      <Alert severity={deleteFeedback.severity} onClose={handleDeleteFeedbackClose} sx={{ width: '100%' }}>
        {deleteFeedback.message}
      </Alert>
    </Snackbar>

  </div>


}


export default StationView
